/* Copyright (c) Trimble Inc. All rights reserved. */

import { Logger } from '@tcp-base/toolkit';
import { JWT_TOKEN_EXPIRATION_BUFFER_SECONDS } from '../config/CONSTANTS';
import { useTokenStore } from '../stores/Token.store';
import { JWTParseHelper } from './JWTParser.helper';

/**
 * Token utility functions for authentication
 */

class TokenValidationHelperImpl {
  constructor() {
    // constructor to prevent instantiation
  }
  /**
   * Get access token from TokenStore with IndexedDB fallback
   */
  public getAccessToken = async (): Promise<string | null> => {
    try {
      // Use TokenStore's getTokenDetails() which has IndexedDB fallback logic
      const tokenDetails = await useTokenStore.getState()?.getTokenDetails();

      return tokenDetails.access_token || null;
    } catch (error) {
      Logger.warn('Failed to get access token from TokenStore:', error);
      return null;
    }
  };

  /**
   * Check if token is expired (delegates to JWTParser helper)
   */
  public isTokenExpired = (token: string): boolean => {
    try {
      return JWTParseHelper.isJWTExpired(token, JWT_TOKEN_EXPIRATION_BUFFER_SECONDS);
    } catch (error) {
      Logger.warn('Failed to check token expiration:', error);
      return true; // Consider invalid tokens as expired
    }
  };

  /**
   * Validate if access token exists and is not expired (async version)
   */
  public validateAccessToken = async (): Promise<{
    exists: boolean;
    expired: boolean;
    valid: boolean;
    token: string | null;
  }> => {
    try {
      // Get token details directly to distinguish between different scenarios
      const tokenDetails = await useTokenStore.getState()?.getTokenDetails();

      // If tokenDetails is undefined/null, no token store data exists
      if (!tokenDetails) {
        return {
          exists: false,
          expired: false, // No token details means nothing to expire
          valid: false,
          token: null,
        };
      }

      // If tokenDetails exists, check for access_token
      const rawToken = tokenDetails.access_token;
      const token = rawToken || null;
      const exists = !!rawToken;

      // Check if token is whitespace-only or invalid
      const isWhitespaceOnly = rawToken && rawToken.trim() === '';

      // If tokenDetails exists but no access_token or whitespace-only, consider it expired state
      // If token exists and is not whitespace-only, check if it's actually expired
      const expired = !token || isWhitespaceOnly ? true : this.isTokenExpired(token);
      const valid = exists && !expired && !isWhitespaceOnly;

      return {
        exists,
        expired,
        valid,
        token: rawToken || null,
      };
    } catch (error) {
      // If there's an error accessing the store, treat as expired state
      Logger.warn('Failed to validate access token:', error);
      return {
        exists: false,
        expired: true,
        valid: false,
        token: null,
      };
    }
  };
}

export const TokenValidationHelper = new TokenValidationHelperImpl();
export default TokenValidationHelper;
